// src/pages/Eventos.js
import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Edit, Trash2, Eye, Search, Filter } from 'lucide-react';
import { eventosEmergenciaAPI } from '../services/apiService';
import ModalEvento from '../components/eventos/ModalEvento';
import ModalVerEvento from '../components/eventos/ModalVerEvento';

const Eventos = () => {
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [filterAño, setFilterAño] = useState('');

  // Estados para los modales
  const [modalRegistro, setModalRegistro] = useState(false);
  const [modalEditar, setModalEditar] = useState(false);
  const [modalVer, setModalVer] = useState(false);
  const [eventoSeleccionado, setEventoSeleccionado] = useState(null);

  useEffect(() => {
    loadEventos();
  }, []);

  const loadEventos = async () => {
    try {
      setLoading(true);
      const response = await eventosEmergenciaAPI.getAll();
      
      console.log('📥 Respuesta de eventos:', response);
      
      // Manejar diferentes formatos de respuesta
      let data;
      if (Array.isArray(response)) {
        // Si es array directo
        data = response;
      } else if (response?.success && response?.data) {
        // Si tiene estructura {success, data}
        data = response.data;
      } else if (response && typeof response === 'object' && !response.success) {
        // Si es un objeto sin success, asumir que es el data directamente
        data = response;
      } else {
        // Fallback
        data = [];
      }
      
      if (Array.isArray(data)) {
        setEventos(data);
        setError(null);
      } else {
        console.error('❌ Formato de respuesta inesperado:', data);
        setError('Error: Formato de datos inválido');
        setEventos([]);
      }
    } catch (err) {
      console.error('❌ Error cargando eventos:', err);
      setError(err.response?.data?.message || err.message || 'No se pudo conectar con el servidor');
      setEventos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este evento? Esto puede afectar las emergencias asociadas.')) {
      try {
        console.log('Eliminar evento:', id);
        alert('Funcionalidad de eliminación pendiente de implementar en la API');
      } catch (err) {
        console.error('Error al eliminar:', err);
        alert('Error al eliminar el evento');
      }
    }
  };

  const handleVer = (evento) => {
    setEventoSeleccionado(evento);
    setModalVer(true);
  };

  const handleEditar = (evento) => {
    setEventoSeleccionado(evento);
    setModalEditar(true);
  };

  const handleNuevo = () => {
    setEventoSeleccionado(null);
    setModalRegistro(true);
  };

  const handleSuccess = () => {
    loadEventos();
  };

  // Obtener años únicos para el filtro
  const añosUnicos = [...new Set(eventos.map(e => e.Año))].sort((a, b) => b - a);
  
  // Obtener tipos únicos para el filtro
  const tiposUnicos = [...new Set(eventos.map(e => e.Tipo))].filter(Boolean).sort();

  // Filtrar eventos
  const filteredEventos = eventos.filter(evento => {
    const matchSearch = !searchTerm || 
      evento.Fenomeno?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      evento.Tipo?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchTipo = !filterTipo || evento.Tipo === filterTipo;
    const matchAño = !filterAño || evento.Año?.toString() === filterAño;
    
    return matchSearch && matchTipo && matchAño;
  });

  const getEventIcon = (tipo) => {
    const icons = {
      'Huracán': '🌀',
      'Tormenta Tropical': '⛈️',
      'Inundación': '🌊',
      'Deslizamiento': '⛰️',
      'Sequía': '☀️',
      'Incendio': '🔥',
      'Terremoto': '🌍'
    };
    return icons[tipo] || '⚠️';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c8a88c] mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando eventos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
        <button 
          onClick={loadEventos}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Eventos de Emergencia</h1>
          <p className="text-gray-500 mt-1">Fenómenos y eventos</p>
        </div>
        <button 
          onClick={handleNuevo}
          className="flex items-center gap-2 px-4 py-2 bg-[#c8a88c] text-white rounded-lg font-medium hover:bg-[#b8987c] shadow-md transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nuevo Evento
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Búsqueda */}
          <div className="md:col-span-2 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por fenómeno o tipo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-50 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent focus:outline-none"
            />
          </div>

          {/* Filtro por Tipo */}
          <div>
            <select
              value={filterTipo}
              onChange={(e) => setFilterTipo(e.target.value)}
              className="w-full px-4 py-2 bg-gray-50 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent focus:outline-none"
            >
              <option value="">Todos los tipos</option>
              {tiposUnicos.map(tipo => (
                <option key={tipo} value={tipo}>{tipo}</option>
              ))}
            </select>
          </div>

          {/* Filtro por Año */}
          <div>
            <select
              value={filterAño}
              onChange={(e) => setFilterAño(e.target.value)}
              className="w-full px-4 py-2 bg-gray-50 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent focus:outline-none"
            >
              <option value="">Todos los años</option>
              {añosUnicos.map(año => (
                <option key={año} value={año}>{año}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Filtros activos */}
        {(filterTipo || filterAño) && (
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
            <span className="text-sm text-gray-600">Filtros activos:</span>
            {filterTipo && (
              <span className="px-3 py-1 bg-[#c8a88c]/10 text-[#c8a88c] rounded-full text-sm flex items-center gap-2">
                {filterTipo}
                <button onClick={() => setFilterTipo('')} className="hover:text-[#b8987c]">×</button>
              </span>
            )}
            {filterAño && (
              <span className="px-3 py-1 bg-[#c8a88c]/10 text-[#c8a88c] rounded-full text-sm flex items-center gap-2">
                Año {filterAño}
                <button onClick={() => setFilterAño('')} className="hover:text-[#b8987c]">×</button>
              </span>
            )}
            <button 
              onClick={() => { setFilterTipo(''); setFilterAño(''); }}
              className="text-sm text-gray-500 hover:text-gray-700 ml-2"
            >
              Limpiar filtros
            </button>
          </div>
        )}
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-12"></th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Fenómeno</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Año</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Fecha Inicio</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Fecha Fin</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredEventos.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">
                      {searchTerm || filterTipo || filterAño 
                        ? 'No se encontraron eventos con los filtros aplicados' 
                        : 'No hay eventos registrados'}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredEventos.map((evento) => (
                  <tr key={evento.Id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <span className="text-2xl">{getEventIcon(evento.Tipo)}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{evento.Fenomeno}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                        {evento.Tipo || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {evento.Año}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {evento.FechaInicio 
                        ? new Date(evento.FechaInicio).toLocaleDateString() 
                        : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {evento.FechaFin 
                        ? new Date(evento.FechaFin).toLocaleDateString() 
                        : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleVer(evento)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Ver"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEditar(evento)}
                          className="p-2 text-[#c8a88c] hover:bg-[#c8a88c]/10 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(evento.Id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info de resultados */}
      {filteredEventos.length > 0 && (
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>
            Mostrando {filteredEventos.length} de {eventos.length} eventos
          </span>
          <span>
            Total: {eventos.length} eventos registrados
          </span>
        </div>
      )}

      {/* Modales */}
      <ModalEvento
        isOpen={modalRegistro}
        onClose={() => setModalRegistro(false)}
        onSuccess={handleSuccess}
      />

      <ModalEvento
        isOpen={modalEditar}
        onClose={() => setModalEditar(false)}
        evento={eventoSeleccionado}
        onSuccess={handleSuccess}
      />

      <ModalVerEvento
        isOpen={modalVer}
        onClose={() => setModalVer(false)}
        evento={eventoSeleccionado}
      />
    </div>
  );
};

export default Eventos;